import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Row, Col, Card, Form, Input, Button, Select, App, Space, Checkbox } from 'antd';
import {
  MailOutlined,
  EnvironmentOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { PublicPageLayout } from '../../components/public/PublicPageLayout';
import { SEOHead } from '../../components/SEOHead';
import '../../styles/contact.css';
import '../../styles/public-buttons.css';
import { phoneNumberRule } from '@/constants/validation';
import { trpc } from '../../lib/api';
import { PhoneInput, EmailInput } from '../../components/common';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type InquiryType =
  | 'support'
  | 'other'
  | 'security'
  | 'billing'
  | 'plan'
  | 'request'
  | 'trial'
  | 'feature'
  | 'demo'
  | 'login_support';

interface ContactFormValues {
  company: string;
  department?: string;
  name: string;
  email: string;
  emailConfirm: string;
  phone: string; // 必須に変更
  inquiryType: InquiryType;
  message: string;
  agreeToPrivacy: boolean;
}

// お問い合わせフォーム
const ContactFormSection: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm<ContactFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const submitMutation = trpc.contact.submit.useMutation({
    onSuccess: () => {
      message.success('お問い合わせを受け付けました。担当者から折り返しご連絡いたします。');
      form.resetFields();
      setSubmitting(false);
    },
    onError: (error) => {
      message.error(error.message || '送信に失敗しました。もう一度お試しください。');
      setSubmitting(false);
    },
  });

  const handleSubmit = async (values: ContactFormValues) => {
    setSubmitting(true);
    submitMutation.mutate(values);
  };

  return (
    <section className="contact-form-section">
      <div className="section-container">
        <Row gutter={[48, 48]}>
          <Col xs={24} lg={14}>
            <Card className="contact-form-card">
              <Title level={3}>お問い合わせフォーム</Title>
              <Paragraph type="secondary">
                以下のフォームからお問い合わせください。担当者より2営業日以内にご連絡いたします。
              </Paragraph>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
              >
                <Form.Item
                  name="company"
                  label="会社名"
                  rules={[{ required: true, message: '会社名を入力してください' }]}
                >
                  <Input size="large" placeholder="株式会社〇〇" />
                </Form.Item>

                <Form.Item
                  name="department"
                  label="部署名（任意）"
                >
                  <Input size="large" placeholder="製造部" />
                </Form.Item>

                <Form.Item
                  name="name"
                  label="お名前"
                  rules={[{ required: true, message: 'お名前を入力してください' }]}
                >
                  <Input size="large" placeholder="山田 太郎" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="メールアドレス"
                  rules={[
                    { required: true, message: 'メールアドレスを入力してください' },
                    { type: 'email', message: '有効なメールアドレスを入力してください' },
                  ]}
                >
                  <EmailInput size="large" placeholder="example@company.com" />
                </Form.Item>

                <Form.Item
                  name="emailConfirm"
                  label="メールアドレス（確認用）"
                  dependencies={['email']}
                  rules={[
                    { required: true, message: 'メールアドレス（確認用）を入力してください' },
                    { type: 'email', message: '有効なメールアドレスを入力してください' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('email') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('メールアドレスが一致しません'));
                      },
                    }),
                  ]}
                >
                  <EmailInput size="large" placeholder="example@company.com" />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label="電話番号"
                  rules={[
                    { required: true, message: '電話番号を入力してください' },
                    { max: 20, message: '電話番号は20文字以内で入力してください' },
                    phoneNumberRule('有効な電話番号を入力してください'),
                  ]}
                >
                  <PhoneInput size="large" placeholder="03-1234-5678" />
                </Form.Item>

                <Form.Item
                  name="inquiryType"
                  label="お問い合わせ種別"
                  rules={[{ required: true, message: 'お問い合わせ種別を選択してください' }]}
                >
                  <Select size="large" placeholder="選択してください">
                    <Select.Option value="trial">無料登録について</Select.Option>
                    <Select.Option value="plan">料金プランについて</Select.Option>
                    <Select.Option value="feature">機能について</Select.Option>
                    <Select.Option value="demo">デモ・説明会の依頼</Select.Option>
                    <Select.Option value="support">導入サポートについて</Select.Option>
                    <Select.Option value="login_support">登録後サポート</Select.Option>
                    <Select.Option value="billing">請求について</Select.Option>
                    <Select.Option value="request">要望について</Select.Option>
                    <Select.Option value="security">セキュリティについて</Select.Option>
                    <Select.Option value="other">その他</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="message"
                  label="お問い合わせ内容"
                  rules={[{ required: true, message: 'お問い合わせ内容を入力してください' }]}
                >
                  <TextArea
                    rows={6}
                    placeholder="お問い合わせ内容を詳しくご記入ください"
                  />
                </Form.Item>

                <Form.Item
                  name="agreeToPrivacy"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error('個人情報保護方針および個人情報の取り扱いについてに同意してください')),
                    },
                  ]}
                >
                  <Checkbox>
                    <a href="https://dio-meta.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
                      個人情報保護方針および個人情報の取り扱いについて
                    </a>
                    に同意する
                  </Checkbox>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting}
                    icon={<SendOutlined />}
                  >
                    送信する
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Card className="contact-info-card">
                <Title level={4}>運営会社</Title>
                <div className="contact-info-item">
                  <EnvironmentOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                  <div>
                    <Text strong>株式会社DiO</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      〒602-8061<br />
                      京都府京都市上京区甲斐守町97番地<br />
                      西陣産業創造會舘
                    </Text>
                  </div>
                </div>

                <div className="contact-info-item">
                  <MailOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                  <div>
                    <Text strong>メール</Text>
                    <br />
                    <a href="mailto:info@ichigen-cho.com" style={{ fontSize: 14 }}>
                      info@ichigen-cho.com
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="contact-notice-card">
                <Title level={5}>お問い合わせ前に</Title>
                <Paragraph style={{ fontSize: 13, margin: 0 }}>
                  よくあるご質問は<a href="/service/features">サービス詳細ページ</a>
                  や<a href="/service/plans">料金プランページ</a>のFAQセクションでご確認いただけます。
                </Paragraph>
              </Card>

              <Card className="contact-trial-card">
                <Title level={5}>まずは無料で試してみませんか?</Title>
                <Paragraph style={{ fontSize: 13, marginBottom: 16 }}>
                  無料登録で全機能をお試しいただけます。
                </Paragraph>
                <Link to="/register" className="public-btn public-btn--primary public-btn--block">
                  無料登録を開始
                </Link>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </section>
  );
};

// メインコンポーネント
export const ContactPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="お問い合わせ｜一元帳"
        description="一元帳に関するお問い合わせはこちら。導入相談や機能に関するご質問を受け付けています。"
      />
      <PublicPageLayout>
        <div className="contact-page">
        <div className="contact-header">
          <div className="section-container">
            <div className="contact-header-content">
              <Title level={1}>お問い合わせ</Title>
              <Paragraph className="contact-subtitle">
                サービスに関するご質問、導入のご相談など、お気軽にお問い合わせください
              </Paragraph>
            </div>
          </div>
        </div>

        <ContactFormSection />
      </div>
    </PublicPageLayout>
    </>
  );
};
